import { useEffect, useState } from 'react'
import {
  Control,
  Controller,
  FieldErrors,
  Path,
  PathValue,
  UseFormGetValues,
  UseFormRegister,
  UseFormReset,
  UseFormSetValue,
} from 'react-hook-form'
import { useMutation } from 'urql'

import IconCheck from '@/assets/icons/ic_logic-g-app_checkbox.svg'
import { LicenseCode } from '@/components/license-code'
import { BirthdatePicker } from '@/components/ui/birthdate-picker'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { VerificationField } from '@/gql/generated/graphql'
import { SchoolCodeCheckMutation } from '@/queries'
import { SignupFormData } from '@/screens/SignupForm'
import { cn } from '@/utils/tw-helpers'

import { SchoolCodeRegistrationFormData } from '../modal/SchoolCodeRegistrationModal'

interface Props<T extends SignupFormData | SchoolCodeRegistrationFormData> {
  className?: string
  control: Control<T, any>
  errors: FieldErrors<T>
  register: UseFormRegister<T>
  setValue: UseFormSetValue<T>
  reset?: UseFormReset<T>
  getValues: UseFormGetValues<T>

  schoolCode: string[]
  setSchoolCode: (value: string[]) => void

  validSchoolCode: boolean | undefined
  setValidSchoolCode: (value: boolean | undefined) => void
}

export const SchoolCodeForm = <T extends SignupFormData | SchoolCodeRegistrationFormData>({
  className,
  control,
  errors,
  register,
  setValue,
  getValues,
  validSchoolCode,
  setValidSchoolCode,
}: Props<T>): JSX.Element => {
  const [verificationFields, setVerificationFields] = useState<VerificationField[]>([])

  const [{ data: schoolCodeData }, checkSchoolCodeMutation] = useMutation(SchoolCodeCheckMutation)

  const handleSchoolCodeCheck = async () => {
    const schoolCodeValue = getValues('schoolCode' as Path<T>)

    const result = await checkSchoolCodeMutation({
      input: {
        schoolCode: schoolCodeValue as string,
      },
    })

    if (result.error || result.data?.schoolCodeCheck?.errors) return

    const nextVerificationFields = result.data?.schoolCodeCheck?.verificationFields ?? []

    setVerificationFields(nextVerificationFields)
    setValue('verificationFields' as Path<T>, nextVerificationFields as PathValue<T, Path<T>>)
  }

  useEffect(() => {
    const isValid = schoolCodeData?.schoolCodeCheck?.valid

    setValidSchoolCode(isValid ?? undefined)

    if (isValid === false) {
      setVerificationFields([])
      setValue('verificationFields' as Path<T>, [] as PathValue<T, Path<T>>)
    }
  }, [schoolCodeData?.schoolCodeCheck?.valid, setValue])

  return (
    <div className={className}>
      <Label htmlFor="schoolCode" className="mb-2 text-primary-text">
        スクールコード
      </Label>
      <div className="flex gap-x-3">
        <Controller
          control={control}
          name={'schoolCode' as Path<T>}
          render={({ field: { onBlur, onChange, value } }) => (
            <LicenseCode
              defaultValue={value as string}
              codeLength={12}
              perGroup={4}
              placeholder="0000"
              containerClassName="flex gap-1.5 flex-1"
              inputClassName={cn(
                'license-input w-[49px] h-[48px] rounded-md border border-outline text-center text-sm outline-none flex-1',
                'placeholder:text-secondary-93 focus:ring',
                validSchoolCode == false && 'border-caution text-caution',
                validSchoolCode &&
                  'bg-[#EBF6F2] border-transparent text-attention pointer-events-none'
              )}
              disabled={validSchoolCode ?? false}
              onBlur={onBlur}
              onCodeChange={onChange}
            />
          )}
        />

        <Button
          type="button"
          variant="light"
          className="ml-auto w-full max-w-[86px] self-end px-1 py-1 text-[15px]"
          onClick={() => {
            if (validSchoolCode) {
              setValidSchoolCode(undefined)
              setVerificationFields([])
              setValue('verificationFields' as Path<T>, [] as PathValue<T, Path<T>>)
              setValue('verificationMemberId' as Path<T>, '' as PathValue<T, Path<T>>)
              setValue('verificationBirthdate' as Path<T>, '' as PathValue<T, Path<T>>)
            } else {
              handleSchoolCodeCheck()
            }
          }}
        >
          {validSchoolCode ? '適用解除' : '適用'}
        </Button>
      </div>

      {validSchoolCode && (
        <p className="mt-3 flex items-center gap-2 text-xs text-attention">
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-attention text-white">
            <IconCheck width={18} />
          </span>
          {schoolCodeData?.schoolCodeCheck?.schoolName}
        </p>
      )}
      {validSchoolCode === false && (
        <p className="mt-3 text-xs text-caution">入力されたコードが正しくありません</p>
      )}

      {validSchoolCode && (
        <>
          {verificationFields.includes('MEMBER_ID') && (
            <div className="mt-4">
              <Label htmlFor="verificationMemberId" className="mb-2 text-primary-text">
                会員ID <span className="text-caution">※</span>
              </Label>

              <Input
                type="text"
                error={!!errors.verificationMemberId}
                placeholder="会員ID"
                {...register('verificationMemberId' as Path<T>)}
              />
              {!!errors.verificationMemberId && (
                <p className="mt-1 text-[10px] leading-tight text-caution md:text-xs">
                  {errors.verificationMemberId.message as string}
                </p>
              )}
            </div>
          )}

          {verificationFields.includes('BIRTHDATE') && (
            <div className="mt-4">
              <Label htmlFor="verificationBirthdate" className="mb-2 text-primary-text">
                お子様の生年月日 <span className="text-caution">※</span>
              </Label>

              <Controller
                control={control}
                name={'verificationBirthdate' as Path<T>}
                render={({ field: { onChange } }) => (
                  <BirthdatePicker
                    onValueChange={onChange}
                    error={!!errors.verificationBirthdate}
                  />
                )}
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}
